import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, SparklesIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from './ContactFormPopup';
export function SaleServices() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  return <>
      <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-288">
        <div className="relative z-10 mx-auto max-w-7xl" data-id="element-289">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="mb-12 sm:mb-16 text-center" data-id="element-290">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-291">
              {t('sale.pricing.title')}{' '}
              <span style={{
              color: 'var(--accent)'
            }} data-id="element-292">
                {t('sale.pricing.titleHighlight')}
              </span>
            </h2>
            <p className="mx-auto max-w-2xl text-base sm:text-lg" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-293">
              {t('sale.pricing.subtitle')}
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid gap-6 lg:grid-cols-2 max-w-5xl mx-auto" data-id="element-294">
            {/* Free Demo Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-295">
              <div className="h-full rounded-3xl border p-8 flex flex-col" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-296">
                <div className="flex items-center gap-3 mb-6" data-id="element-297">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-298">
                    <SparklesIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-299" />
                  </div>
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-300">
                    {t('pricing.demo.title')}
                  </h3>
                </div>

                <div className="mb-6" data-id="element-301">
                  <div className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-2" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff'
                }} data-id="element-302">
                    {t('pricing.demo.label')}
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-303">
                    {t('pricing.demo.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1" data-id="element-304">
                  {[1, 2, 3].map(i => <li key={i} className="flex items-start gap-3" data-id="element-305">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-306">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-307" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-308">
                        {t(`pricing.demo.b${i}`)}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02]" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-309">
                  {t('pricing.demo.cta')}
                </button>
              </div>
            </motion.div>

            {/* Website Subscription */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-310">
              <div className="h-full rounded-3xl border p-8 flex flex-col relative overflow-hidden" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-311">
                <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-312">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-313">
                    <RocketIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-314" />
                  </div>
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-315">
                    {t('sale.pricing.launch.title')}
                  </h3>
                </div>

                <div className="mb-6 relative z-10" data-id="element-316">
                  <div className="flex items-baseline gap-2 flex-wrap" data-id="element-317">
                    <span className="text-2xl font-bold line-through opacity-50" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-318">
                      €25
                    </span>
                    <span className="text-4xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-319">
                      {t('sale.pricing.launch.price')}
                    </span>
                    <span className="text-sm" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-320">
                      {t('sale.pricing.launch.period')}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-321">
                    {t('sale.pricing.launch.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1 relative z-10" data-id="element-322">
                  {[1, 2, 3, 4].map(i => <li key={i} className="flex items-start gap-3" data-id="element-323">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-324">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-325" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-326">
                        {t(`pricing.launch.b${i}`)}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02] relative z-10" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-327">
                  {t('pricing.launch.cta')}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-328" />
    </>;
}